import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap';
import OrgUserService from '../../../services/orgUser.service';
import '../../../styles/dashboard/OrgUser/KycVerification.css';

export default function KycPanVerification() {
  const [activeTab, setActiveTab] = useState('pan');
  const [panNumber, setPanNumber] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;

  const [filterForm, setFilterForm] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    status: 'all'
  });
  const [filters, setFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    status: 'all'
  });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);

  const [panVerificationCost, setPanVerificationCost] = useState(5);

  const getErrorMessage = (err, fallback) => {
    const detailsMessage = err?.details?.message || err?.details?.error || '';
    const hint = err?.hint ? ` ${err.hint}` : '';
    return `${err?.message || err?.error || detailsMessage || fallback}${hint}`.trim();
  };

  // Fetch cost on mount
  useEffect(() => {
    const fetchCost = async () => {
      try {
        const res = await OrgUserService.getCreditCosts();
        const cost = res?.data?.panVerificationCost || 5;
        setPanVerificationCost(cost);
      } catch (err) {
        console.error('Failed to fetch cost:', err);
      }
    };
    fetchCost();
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const normalizedStatus = filters.status === 'all' ? '' : filters.status;
      const res = await OrgUserService.getPanVerifications(
        currentPage,
        perPage,
        filters.search,
        filters.fromDate,
        filters.toDate,
        normalizedStatus
      );
      const list = res.data || res?.data?.data || [];
      setHistoryItems(list);
      const total = res.pagination?.totalRecords || res.pagination?.total || list.length;
      setTotalRecords(total);
    } catch (err) {
      setHistoryItems([]);
      setTotalRecords(0);
    }
    setHistoryLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitMessage('');

    const normalizedPan = panNumber.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(normalizedPan)) {
      setSubmitError('PAN Number format is invalid (e.g., ABCDE1234F)');
      return;
    }

    setSubmitting(true);
    try {
      const res = await OrgUserService.createPanVerification(normalizedPan);
      const payload = res?.data?.verification || res?.data?.data?.verification;

      if (payload?.error && !payload?.rawResponse) {
        setSubmitError(payload.error);
        setSubmitting(false);
        return;
      }

      const details = {
        id: res?.data?.id || res?.data?.data?.id,
        reportId: res?.data?.reportId || res?.data?.data?.reportId,
        panNumber: res?.data?.panNumber || res?.data?.data?.panNumber || normalizedPan,
        customerName: res?.data?.customerName || res?.data?.data?.customerName,
        verified: res?.data?.verified || res?.data?.data?.verified,
        payload
      };

      setVerificationDetails(details);
      setSuccessModalOpen(true);
      setSubmitMessage('PAN verification submitted. Report is being generated.');
      setPanNumber('');
      setActiveTab('history');
      setCurrentPage(1);
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Failed to verify PAN'));
    }
    setSubmitting(false);
  };

  const handleViewReport = async (verificationId) => {
    try {
      const res = await OrgUserService.downloadPanVerificationReport(verificationId);
      const url = res?.data?.url || res?.data?.downloadUrl || res?.data?.data?.url;
      const pdfReady = res?.data?.pdfReady ?? res?.data?.data?.pdfReady;

      if (!pdfReady) {
        alert('PDF is still being generated. Please try again in a few moments.');
        return;
      }

      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        alert('Report URL not available yet. Please try again.');
      }
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to load PAN report'));
    }
  };

  const renderResponseDetails = () => {
    const raw = verificationDetails?.payload?.rawResponse || null;
    const data = raw?.data || {};

    if (!raw) {
      return <div className="kyc-empty">Response details not available.</div>;
    }

    return (
      <div className="kyc-response">
        <div className="kyc-response-row"><span>Code</span><strong>{raw.code}</strong></div>
        <div className="kyc-response-row"><span>Timestamp</span><strong>{raw.timestamp}</strong></div>
        <div className="kyc-response-row"><span>Transaction ID</span><strong>{raw.transaction_id}</strong></div>
        <div className="kyc-response-row"><span>Sub Code</span><strong>{raw.sub_code}</strong></div>
        <div className="kyc-response-row"><span>Message</span><strong>{raw.message}</strong></div>

        <div className="kyc-response-section">Details</div>
        <div className="kyc-response-grid">
          <div><span>PAN Number</span><strong>{data.pan_number}</strong></div>
          <div><span>Full Name</span><strong>{data.full_name}</strong></div>
          <div><span>Masked Aadhaar</span><strong>{data.masked_aadhaar}</strong></div>
          <div><span>Email</span><strong>{data.email}</strong></div>
          <div><span>Phone</span><strong>{data.phone_number}</strong></div>
          <div><span>Gender</span><strong>{data.gender}</strong></div>
          <div><span>Date of Birth</span><strong>{data.dob}</strong></div>
          <div><span>Aadhaar Linked</span><strong>{String(data.aadhaar_linked)}</strong></div>
          <div><span>Category</span><strong>{data.category}</strong></div>
          <div className="kyc-response-span">
            <span>Address</span>
            <strong>{data.address?.full}</strong>
          </div>
        </div>
      </div>
    );
  };

  const totalPages = Math.ceil(totalRecords / perPage) || 1;
  const modalTitle = verificationDetails?.verified ? 'PAN Verified Successfully' : 'PAN Verification Completed';

  const handleFilterChange = (name, value) => {
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setFilters(filterForm);
  };

  const handleResetFilters = () => {
    const next = { search: '', fromDate: '', toDate: '', status: 'all' };
    setFilterForm(next);
    setFilters(next);
    setCurrentPage(1);
  };

  const handleCloseSuccessModal = async () => {
    setSuccessModalOpen(false);
    setSubmitMessage('');
    setSubmitError('');
    setVerificationDetails(null);

    if (activeTab !== 'history') {
      setActiveTab('history');
      setCurrentPage(1);
    }

    await loadHistory();
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <div>
          <h1>PAN Verification</h1>
          <p>Verify PAN details .</p>
        </div>
      </div>

      <div className="kyc-tabs">
        <button
          className={`kyc-tab-btn ${activeTab === 'pan' ? 'active' : ''}`}
          onClick={() => setActiveTab('pan')}
        >
          PAN Verification
        </button>
        <button
          className={`kyc-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {activeTab === 'pan' ? (
        <form className="kyc-card" onSubmit={handleSubmit}>
          <div className="kyc-card-title">PAN Verification</div>
          <p className="kyc-card-desc">
            Enter the PAN number to start verification.
            <br />
            <strong>Cost: ₹{panVerificationCost}</strong> per verification
          </p>

          <div className="kyc-form-group">
            <label htmlFor="panNumber">PAN Number</label>
            <input
              id="panNumber"
              name="panNumber"
              type="text"
              value={panNumber}
              onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
              maxLength={10}
              autoComplete="off"
              className="kyc-input"
            />
          </div>

          {submitError && <div className="kyc-error">{submitError}</div>}
          {submitMessage && <div className="kyc-success">{submitMessage}</div>}

          <button className="kyc-primary-btn" type="submit" disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify PAN'}
          </button>
        </form>
      ) : (
        <div className="kyc-history">
          <div className="kyc-filters">
            <input
              type="text"
              className="kyc-input"
              placeholder="Search"
              value={filterForm.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            <input
              type="date"
              className="kyc-input"
              value={filterForm.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
            <input
              type="date"
              className="kyc-input"
              value={filterForm.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
            <select
              className="kyc-input"
              value={filterForm.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
            <div className="kyc-filter-actions">
              <button type="button" onClick={handleApplyFilters}>
                Apply
              </button>
              <button type="button" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>
          {historyLoading ? (
            <div className="kyc-empty">Loading history...</div>
          ) : (
            <>
              <div className="kyc-table-wrapper">
                <table className="kyc-table">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Report Id</th>
                      <th>Customer Name</th>
                      <th>Date &amp; Time</th>
                      <th>Cost</th>
                      <th>Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="kyc-empty-cell">No history available.</td>
                      </tr>
                    ) : (
                      historyItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{(currentPage - 1) * perPage + index + 1}</td>
                          <td>{item.reportId || 'N/A'}</td>
                          <td>{item.customerName || 'N/A'}</td>
                          <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}</td>
                          <td>₹{item.cost || 0}</td>
                          <td>
                            <button
                              type="button"
                              className="kyc-link-btn"
                              onClick={() => handleViewReport(item.id)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="kyc-pagination">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                >
                  Prev
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={successModalOpen}
        toggle={handleCloseSuccessModal}
        centered
        className="kyc-success-modal"
      >
        <ModalHeader toggle={handleCloseSuccessModal}>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          <div className="kyc-success-summary">
            <div>
              <div className="kyc-success-name">{verificationDetails?.customerName || 'N/A'}</div>
              <div className="kyc-success-pan">PAN: {verificationDetails?.panNumber || 'N/A'}</div>
            </div>
          </div>
          {renderResponseDetails()}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => handleViewReport(verificationDetails?.id)}
          >
            View Report
          </Button>
          <Button color="secondary" onClick={handleCloseSuccessModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
